import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LiveCourtCard from '../components/organizer/LiveCourtCard';
import CourtManager from '../components/organizer/CourtManager';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { 
    getEventMatches, 
    startMatch, 
    finishMatch, 
    getEventRegistrations, 
    getEvent, 
    updateEventAssignments, 
    createMatch,
    updateEvent,
    updateMatch,
    deleteMatch
} from '../services/api';
import { useWebSocket } from '../hooks/useRealtime';
import toast from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';
import { useWakeLock } from '../hooks/useWakeLock';

/**
 * LiveManagement - Página de Gestión en Vivo
 * 
 * Incluye gestión de energía (Wake Lock) para evitar bloqueo de pantalla
 */
const LiveManagement = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    // Wake Lock Hook
    const { requestWakeLock, releaseWakeLock } = useWakeLock();


    // Estado del evento
    const [eventStatus, setEventStatus] = useState('scheduled');
    const [elapsedTime, setElapsedTime] = useState(0);
    const [activeTab, setActiveTab] = useState('live');
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Fetch matches
    const {
        data: matchesData,
        isLoading: isLoadingMatches,
        isError: isErrorMatches,
        error: errorMatches
    } = useQuery({
        queryKey: ['eventMatches', eventId],
        queryFn: () => getEventMatches(eventId),
        refetchInterval: 10000,
    });

    const matches = matchesData || [];

    // Fetch Event Details
    const { 
        data: eventData = {},
        isLoading: isLoadingEvent 
    } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => getEvent(eventId),
        staleTime: 0,
        refetchInterval: 5000,
    });

    const event = { ...eventData, title: eventData.title || 'Cargando...' };

    const { data: registrations = [] } = useQuery({
        queryKey: ['registrations', eventId],
        queryFn: () => getEventRegistrations(eventId),
    });

    const [localAccumulated, setLocalAccumulated] = useState(0);
    
    // Estado consolidado para el timer (Única fuente de verdad)
    const [timerState, setTimerState] = useState({
        timeLeft: null,
        pozoTimeLeft: null,
        currentRoundDisplay: "0/0",
        elapsedTime: 0
    });
    
    const [lastSetSoundPlayed, setLastSetSoundPlayed] = useState({}); // Tracking para no repetir sonidos de sets
    const audioTimeout = React.useRef(null); // Ref para controlar la parada automática
    // NOTA: Se usan fuentes de alta disponibilidad para evitar errores de servidor (404/503)
    // NOTA: Se usan fuentes de alta disponibilidad (Github/CDN) para evitar errores de servidor (404/503)
    const sounds = React.useRef({
        start: new Audio('/assets/sounds/freepik-baile-ad.mp3'), 
        change: new Audio('/assets/sounds/freepik-baile-ad.mp3'),
        end: new Audio('/assets/sounds/freepik-baile-ad.mp3')
    });

    // Forzado de carga y manejo de errores (Anti-503/404)
    useEffect(() => {
        const verifySounds = () => {
            Object.values(sounds.current).forEach(audio => {
                audio.load();
                audio.addEventListener('error', (e) => {
                    console.warn("Audio source failed, staying silent or using backup", e);
                });
            });
        };
        verifySounds();
    }, []);

    // Reproducción de audio SIMPLIFICADA y ROBUSTA (Sin Fades que bloqueen el hilo)
    const playRockyAudio = (audioKey) => {
        try {
            // 1. Detener todo antes de reproducir
            stopAllAudio();

            const audio = sounds.current[audioKey];
            if (!audio) return;

            // 2. Configuración base
            audio.currentTime = 0;
            audio.volume = 1.0;
            audio.loop = false; // Reproducir EXACTAMENTE la duración del archivo

            // Limpiar al terminar
            audio.onended = () => stopAllAudio();

            // 3. Reproducir (con manejo de promesa para evitar error de interacción usuario)
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise.catch(e => console.warn("Audio play blocked by browser policy:", e));
            }

            // 4. Programar parada automática para cortar la repetición final (ajuste manual a 28s)
            // REMOVED: Timeout logic to allow full file playback as per user request
            // if (audioTimeout.current) clearTimeout(audioTimeout.current);
            // audioTimeout.current = setTimeout(() => {
            //     stopAllAudio();
            // }, 28000);

        } catch (err) {
            console.error("Audio error:", err);
        }
    };

    // Detener todo el audio inmediatamente
    const stopAllAudio = () => {
        // Limpiar timeout de seguridad
        if (audioTimeout.current) {
            clearTimeout(audioTimeout.current);
            audioTimeout.current = null;
        }

        // Pausar todos los sonidos
        Object.values(sounds.current).forEach(audio => {
            try {
                if (!audio.paused) {
                    audio.pause();
                    // audio.currentTime = 0; // Removed to prevent "fragment repeat" glitch at the end
                }
            } catch (e) {
                console.warn("Error pausing audio:", e);
            }
        });
        
        // Limpiar toasts acumulados para evitar "ventanas infinitas"
        toast.dismiss();
    };

    // Refs para evitar "Stale Closures" en el setInterval (Evita el congelamiento)
    const eventRef = React.useRef(eventData);
    const accumulatedRef = React.useRef(localAccumulated);
    const statusRef = React.useRef(eventStatus);

    useEffect(() => {
        eventRef.current = eventData;
        accumulatedRef.current = localAccumulated;
        statusRef.current = eventStatus;
    }, [eventData, localAccumulated, eventStatus]);

    // Sync status and local timers
    useEffect(() => {
        if (eventData.status) {
            setEventStatus(eventData.status);
            setLocalAccumulated(eventData.accumulatedMs || 0);
        }
    }, [eventData.status, eventData.accumulatedMs]);

    // Wake Lock Logic - Mantener pantalla encendida
    useEffect(() => {
        if (eventStatus === 'in_progress') {
            requestWakeLock();
        } else {
            releaseWakeLock();
        }
        // Cleanup al desmontar lo hace el hook, pero aquí liberamos si cambia estado
    }, [eventStatus, requestWakeLock, releaseWakeLock]);

    // WebSocket - Optimizando para no saturar
    useWebSocket(eventId, {
        onMatchUpdated: (data) => {
            queryClient.invalidateQueries(['eventMatches', eventId]);
            // LOGICA MODO PISTA ORO: Si cualquier pista llega a 6 juegos, sonar aviso
            if (eventData.gameMode === 'gold_court' && eventStatus === 'in_progress') {
                const totalGames = (data.scorePair1 || 0) + (data.scorePair2 || 0);
                const matchId = data.id;
                
                // Solo sonar si es un set ganador (6-x) y NO se ha tocado ya para este match
                if ((data.scorePair1 >= 6 || data.scorePair2 >= 6) && !lastSetSoundPlayed[matchId]) {
                    playRockyAudio('change');
                    setLastSetSoundPlayed(prev => ({ ...prev, [matchId]: true }));
                    toast.success("🎾 ¡Set completado! Rotación.", { duration: 6000 });
                }
            }
        },
        onMatchFinished: () => queryClient.invalidateQueries(['eventMatches', eventId])
    });

    // Ref para controlar la alerta de fin de ronda y evitar múltiples disparos
    const lastRoundAlertedRef = React.useRef(0);

    // Persistent Timer Logic - Ultra Estable & Optimizada
    useEffect(() => {
        // Función de cálculo unificada
        const calculate = () => {
            const currentEvent = eventRef.current;
            if (!currentEvent || !currentEvent.totalDuration) return;

            const totalDurationMin = (currentEvent.totalDuration || 120);
            const roundDurationMin = (currentEvent.roundDuration || 20);
            
            // Cálculos seguros
            const totalDurationMs = totalDurationMin * 60 * 1000;
            const roundMs = roundDurationMin * 60 * 1000;
            const totalRounds = Math.floor(totalDurationMin / roundDurationMin) || 1;

            const isRunning = statusRef.current === 'in_progress';
            let elapsedMs = accumulatedRef.current;

            if (isRunning && currentEvent.actualStartTime) {
                const startMs = currentEvent.actualStartTime?.toMillis ? currentEvent.actualStartTime.toMillis() : Date.now();
                elapsedMs += (Date.now() - startMs);
            }
            
            const pozoRemaining = Math.max(0, totalDurationMs - elapsedMs);
            let nextTimeLeft = null;
            let nextRoundDisplay = "0/0";
            let nextElapsed = Math.floor(elapsedMs / 1000);

            // 1. FIN DEL POZO
            if (pozoRemaining <= 0 && isRunning) {
                // Solo si no se ha finalizado ya (prevent spam)
                if (statusRef.current !== 'finished') {
                    stopAllAudio();
                    playRockyAudio('end');
                    toast.error("🏁 ¡FIN DEL POZO!", { duration: 8000 });
                    handleFinishEvent();
                }
                return;
            }

            // 2. MICRO TORNEOS (Cambios de turno)
            if (currentEvent.gameMode === 'micro_times') {
                nextTimeLeft = Math.max(0, roundMs - (elapsedMs % roundMs));
                const currentRound = Math.min(totalRounds, Math.floor(elapsedMs / roundMs) + 1);
                nextRoundDisplay = `${currentRound}/${totalRounds}`;

                // LÓGICA DE ALERTA: Solo si estamos en el último segundo Y no hemos avisado de esta ronda
                if (isRunning && nextTimeLeft < 1500 && currentRound > lastRoundAlertedRef.current) {
                    stopAllAudio(); // Limpiar audios anteriores por seguridad
                    playRockyAudio('change');
                    toast("⏱️ ¡CAMBIO DE TURNO!", { duration: 10000, icon: '📢' });
                    
                    // Marcar esta ronda como avisada
                    lastRoundAlertedRef.current = currentRound;
                }
            }

            setTimerState({
                timeLeft: nextTimeLeft,
                pozoTimeLeft: pozoRemaining,
                currentRoundDisplay: nextRoundDisplay,
                elapsedTime: nextElapsed
            });
        };

        // Primera ejecución inmediata
        calculate();

        // Intervalo optimizado para móviles (500ms es suficiente para timers en seg)
        // 200ms era demasiado agresivo y causaba colas de eventos en hilos principales saturados
        let interval = null;
        if (eventStatus === 'in_progress') {
            interval = setInterval(calculate, 500);
        }

        return () => interval && clearInterval(interval);
    }, [eventStatus, eventId, localAccumulated, eventData.gameMode, eventData.totalDuration]);

    // OPTIMIZACIÓN: Mover hooks antes del "return isLoading" para evitar Error #310
    const courtMatches = React.useMemo(() => {
        return (matches || []).reduce((acc, m) => {
            if (!acc[m.courtNumber]) acc[m.courtNumber] = [];
            acc[m.courtNumber].push(m);
            return acc;
        }, {});
    }, [matches]);

    const getActiveCourt = React.useCallback((num) => {
        const list = courtMatches[num] || [];
        return list.find(m => m.status !== 'finished') || list[list.length - 1];
    }, [courtMatches]);

    // Mutation para iniciar partido
    const startMatchMutation = useMutation({
        mutationFn: startMatch,
        onSuccess: () => {
            queryClient.invalidateQueries(['eventMatches', eventId]);
            toast.success('Partido iniciado');
        }
    });

    // Mutation para finalizar partido
    const finishMatchMutation = useMutation({
        mutationFn: ({ matchId, result }) => finishMatch(matchId, result),
        onSuccess: () => queryClient.invalidateQueries(['eventMatches', eventId])
    });

    // Handlers
    const handleSaveAssignments = async (assignments) => {
        await updateEventAssignments(eventId, assignments);
        queryClient.invalidateQueries(['event', eventId]);
    };

    const handleInitializeMatches = async (assignments) => {
        await updateEventAssignments(eventId, assignments);
        
        // NUEVA LÓGICA ROBUSTA (Diffing):
        const promises = [];
        
        // 1. Identificar qué pistas tienen jugadores en la nueva asignación
        const courtsWithNewAssignments = new Set();
        Object.entries(assignments).forEach(([courtNum, slots]) => {
            if ((slots.a1 || slots.a2) || (slots.b1 || slots.b2)) {
                courtsWithNewAssignments.add(parseInt(courtNum));
            }
        });

        // 2. BORRAR partidos de pistas que YA NO deben tener jugadores
        // (Iteramos sobre los partidos que ya existen en DB)
        matches.forEach(m => {
            if (m.status !== 'finished') {
                const courtNum = parseInt(m.courtNumber);
                // Si esta pista no está en la lista de "con nuevas asignaciones", borrar el partido
                if (!courtsWithNewAssignments.has(courtNum)) {
                    console.log(`Limpiando Pista ${courtNum} (No seleccionada en pizarra) - Borrando match ${m.id}`);
                    promises.push(deleteMatch(m.id));
                }
            }
        });

        // 3. CREAR o ACTUALIZAR partidos para las pistas asignadas
        Object.entries(assignments).forEach(([courtNumberStr, slots]) => {
            const courtNumber = parseInt(courtNumberStr);
            const hasPlayers = (slots.a1 || slots.a2) || (slots.b1 || slots.b2);

            if (hasPlayers) {
                // ... (mismo código de construcción de pairs) ...
                const pair1Data = {
                    id: slots.a1?.id || slots.a2?.id || 'manual-1',
                    player1: slots.a1?.p1 || 'Jugador A1',
                    player2: slots.a1?.p2 || slots.a2?.p1 || slots.a2?.p2 || 'Jugador A2',
                };
    
                const pair2Data = {
                    id: slots.b1?.id || slots.b2?.id || 'manual-2',
                    player1: slots.b1?.p1 || 'Jugador B1',
                    player2: slots.b1?.p2 || slots.b2?.p1 || slots.b2?.p2 || 'Jugador B2',
                };

                const existingMatch = matches.find(m => 
                    m.courtNumber == courtNumber && m.status !== 'finished'
                );

                if (existingMatch) {
                    promises.push(updateMatch(existingMatch.id, {
                        pair1: pair1Data,
                        pair2: pair2Data
                    }));
                } else {
                    const matchData = {
                        eventId,
                        courtNumber,
                        status: 'scheduled',
                        pair1: pair1Data,
                        pair2: pair2Data,
                        scorePair1: 0,
                        scorePair2: 0,
                        round: 1
                    };
                    promises.push(createMatch(matchData));
                }
            }
        });

        try {
            await Promise.all(promises);
            queryClient.invalidateQueries(['eventMatches', eventId]);
            toast.success("Partidos sincronizados correctamente");
            setActiveTab('live');
        } catch (e) {
            console.error("Error syncing matches:", e);
            toast.error("Error al sincronizar partidos");
        }
    };

    const formatTime = (ms) => {
        if (ms === null) return '--:--';
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const formatElapsedTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}min`;
    };

    const handleUpdateScore = async (matchId, pairNumber, score) => {
        // Encontrar el match y calcular los nuevos scores
        const match = matches.find(m => m.id === matchId);
        if (!match) return;

        const s1 = pairNumber === 1 ? score : match.scorePair1;
        const s2 = pairNumber === 2 ? score : match.scorePair2;

        try {
            await updateMatchScore(matchId, s1, s2);
        } catch (e) {
            console.error("Error updating score:", e);
        }
    };

    const handleStartMatch = async (data) => {
        playRockyAudio('start');
        await startMatchMutation.mutateAsync(data.matchId);
    };

    const handleFinishMatch = async (data) => {
        const { matchId, scorePair1, scorePair2 } = data;
        await finishMatchMutation.mutateAsync({ matchId, result: { scorePair1, scorePair2 } });
    };

    // Callback memoizado para NO romper React.memo en los hijos
    const handleUpdateScoreWrapper = React.useCallback((courtNum, pairNum, score) => {
        const match = getActiveCourt(parseInt(courtNum));
        if (match) handleUpdateScore(match.id, pairNum, score);
    }, [getActiveCourt]); 



    const handlePauseEvent = async () => {
        const now = Date.now();
        const startMs = eventData.actualStartTime?.toMillis() || now;
        const sessionElapsed = now - startMs;
        const newAccumulated = localAccumulated + sessionElapsed;

        // Optimistic update
        setEventStatus('paused');
        setLocalAccumulated(newAccumulated);

        // Detener música si está sonando
        stopAllAudio();

        await updateEvent(eventId, { 
            status: 'paused',
            accumulatedMs: newAccumulated
        });
        queryClient.invalidateQueries(['event', eventId]);
        toast('Evento pausado', { icon: '⏸️' });
    };

    const handleResumeEvent = async () => {
        // Optimistic update
        setEventStatus('in_progress');

        // Unlock audio context and play start music with fade
        playRockyAudio('start');

        // LÓGICA DE SINCRONIZACIÓN: Iniciar todos los partidos pendientes al mismo tiempo
        const pendingMatches = matches.filter(m => m.status === 'scheduled');
        if (pendingMatches.length > 0) {
            const startPromises = pendingMatches.map(m => startMatch(m.id));
            try {
                await Promise.all(startPromises);
                queryClient.invalidateQueries(['eventMatches', eventId]);
            } catch (e) {
                console.error("Error starting all matches:", e);
            }
        }

        await updateEvent(eventId, { 
            status: 'in_progress', 
            actualStartTime: Timestamp.now() 
        });
        queryClient.invalidateQueries(['event', eventId]);
        toast('¡Pozo e inicios de pista sincronizados!', { icon: '🚀' });
    };

    const handleResetTimer = async () => {
        if (confirm('¿Quieres reiniciar el cronómetro del pozo? Esto reseteará las rondas y el tiempo total.')) {
            // Optimistic
            setLocalAccumulated(0);
            setEventStatus('scheduled');

            // Detener música
            stopAllAudio();

            await updateEvent(eventId, { 
                status: 'scheduled',
                actualStartTime: null,
                accumulatedMs: 0 
            });
            queryClient.invalidateQueries(['event', eventId]);
            toast('Cronómetro reiniciado a valores iniciales', { icon: '🔄' });
        }
    };

    const handleFinishEvent = async () => {
        if (confirm('¿Estás seguro de finalizar el evento?')) {
            stopAllAudio();
            await updateEvent(eventId, { status: 'finished' });
            toast.success('Evento finalizado');
        }
    };

    // Full Screen Logic
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                toast.error(`Error al entrar en pantalla completa: ${err.message}`);
            });
            setIsFullScreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                setIsFullScreen(false);
            }
        }
    };

    useEffect(() => {
        const handleFSChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFSChange);
        return () => document.removeEventListener('fullscreenchange', handleFSChange);
    }, []);

    // --- CALCULATIONS & HOOKS (Must be above early returns) ---
    
    // Calcular pistas configuradas para filtrar visualización
    const getConfiguredCourts = () => {
        if (eventData.numberOfCourts) return parseInt(eventData.numberOfCourts);
        if (eventData.courts) return parseInt(eventData.courts);
        if (eventData.courtCount) return parseInt(eventData.courtCount);
        // Fallback robusto: si no hay config, miramos maxPairs o usamos 4
        if (eventData.maxPairs) return Math.ceil(parseInt(eventData.maxPairs) / 4);
        return 4;
    };
    
    const maxCourts = getConfiguredCourts();
    
    // Optimización: Filtrar pistas visibles
    // Solo mostramos pistas que:
    // 1. Tengan una asignación activa en la pizarra
    // 2. O tengan un partido en curso/pendiente (no finalizado)
    const visibleCourtKeys = React.useMemo(() => {
        // Pistas con jugadores en la pizarra
        const assignmentKeys = Object.entries(eventData.assignments || {})
            .filter(([_, slots]) => (slots.a1 || slots.a2) || (slots.b1 || slots.b2))
            .map(([k]) => parseInt(k));
        
        // Pistas con partidos activos en DB
        const activeMatchKeys = Object.values(courtMatches || {})
            .flatMap(list => list)
            .filter(m => m.status !== 'finished')
            .map(m => parseInt(m.courtNumber));

        // Combinar, eliminar duplicados y respetar el máximo de pistas configurado
        const combined = Array.from(new Set([...assignmentKeys, ...activeMatchKeys]))
            .filter(k => k <= maxCourts);
        
        return combined.sort((a,b) => a-b);
    }, [eventData.assignments, courtMatches, maxCourts]);

    if (isLoadingMatches || isLoadingEvent) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;

    const totalMatches = matches.length;
    const completedMatches = matches.filter(m => m.status === 'finished').length;
    const inProgressMatches = matches.filter(m => m.status === 'live' || m.status === 'in_progress').length;
    const progress = totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0;

    return (
        <div className="min-h-screen bg-[#0F111A] pb-20 font-sans">
            {/* Header - WPT Style Carbon Fiber / Dark Blue - REORGANIZED */}
            <div className="bg-[#1A1D2B] border-b border-gray-800 sticky top-0 z-50 shadow-2xl">
                {/* 1. TOP BAR: Event Details */}
                <div className="bg-[#13151f] border-b border-gray-800 py-2 px-4">
                    <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs md:text-sm text-gray-400">
                        <div className="flex items-center gap-4">
                             <button 
                                onClick={() => navigate('/dashboard/events')}
                                className="w-6 h-6 rounded bg-gray-800 flex items-center justify-center text-white hover:bg-gray-700 shrink-0"
                                title="Volver"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-white uppercase tracking-wider">{event.title}</span>
                                <span className="text-gray-600">|</span>
                                <span className="font-medium text-cyan-400">{event.club?.name}</span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <span>📅 {event.date}</span>
                            </div>
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <span>🎾 {visibleCourtKeys.length} Pistas</span>
                            </div>
                             {eventData.level && (
                                <div className="flex items-center gap-1.5 whitespace-nowrap">
                                    <span>📊 Nivel {eventData.level}</span>
                                </div>
                            )}
                            {eventData.price && (
                                <div className="flex items-center gap-1.5 whitespace-nowrap">
                                    <span>💰 {eventData.price}€</span>
                                </div>
                            )}
                             <span className="text-[10px] bg-cyan-400/10 text-cyan-400 px-1.5 py-0.5 rounded border border-cyan-400/20 italic font-bold uppercase">
                                {eventData.gameMode === 'gold_court' ? 'Pista Oro' : 'Micro Torneo'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 2. MAIN CONTROL BAR */}
                <div className="max-w-7xl mx-auto px-4 py-3 md:h-24 flex flex-col md:flex-row items-center justify-between gap-4">
                    
                    {/* LEFT: Controls (Start/Pause, Reset) */}
                    <div className="flex flex-col gap-2 w-full md:w-auto">
                        {/* INICIAR / PAUSAR */}
                        {eventStatus !== 'in_progress' ? (
                            <button 
                                onClick={handleResumeEvent} 
                                className="w-full md:w-40 bg-[#10B981] hover:bg-[#059669] text-white font-black py-2 px-4 rounded-xl flex justify-center items-center gap-2 shadow-[0_0_15px_rgba(16,185,129,0.2)] transition-all transform active:scale-95 uppercase italic text-xs tracking-widest"
                            >
                                <span>▶️</span> {eventStatus === 'scheduled' ? 'INICIAR' : 'REANUDAR'}
                            </button>
                        ) : (
                            <button 
                                onClick={handlePauseEvent} 
                                className="w-full md:w-40 bg-[#F59E0B] hover:bg-[#D97706] text-white font-black py-2 px-4 rounded-xl flex justify-center items-center gap-2 shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all transform active:scale-95 uppercase italic text-xs tracking-widest"
                            >
                                <span>⏸️</span> PAUSAR
                            </button>
                        )}
                        
                        {/* RESET */}
                        <button 
                            onClick={handleResetTimer} 
                            className="w-full md:w-40 bg-[#374151] hover:bg-[#1F2937] text-gray-300 hover:text-white font-bold py-1.5 px-4 rounded-lg flex justify-center items-center gap-2 transition-colors uppercase text-[10px] tracking-widest border border-gray-700"
                        >
                            <span>🔄</span> RESET
                        </button>
                    </div>

                    {/* CENTER: TIMERS */}
                    <div className="flex-1 flex justify-center w-full">
                        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar py-2">
                             <WPTMetric 
                                value={formatTime(timerState.pozoTimeLeft)} 
                                label="FIN DE POZO" 
                                type="timer"
                                size="large"
                            />
                            
                            {eventData.gameMode === 'micro_times' && (
                                <>
                                    <WPTMetric 
                                        value={formatTime(timerState.timeLeft)} 
                                        label="TIEMPO PARTIDO" 
                                        type="timer_active"
                                        size="large"
                                    />
                                    <WPTMetric 
                                        value={timerState.currentRoundDisplay} 
                                        label="RONDA" 
                                        size="large"
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Display & Actions */}
                    <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                        <button 
                            onClick={toggleFullScreen}
                            className={`
                                h-12 px-4 rounded-xl flex items-center gap-2 font-bold uppercase text-xs tracking-wider transition-all border-2
                                ${isFullScreen 
                                    ? 'bg-red-500/10 border-red-500 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                                    : 'bg-cyan-500/10 border-cyan-500 text-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.2)]'}
                            `}
                        >
                            <span className="text-xl">{isFullScreen ? '🏠' : '📺'}</span>
                            <span className="hidden md:inline">TV MODE</span>
                        </button>

                         <button 
                            onClick={handleFinishEvent} 
                            className="h-12 px-4 bg-[#EF4444] hover:bg-[#DC2626] text-white font-black rounded-xl shadow-lg transition-all transform active:scale-95 flex items-center gap-2 uppercase italic text-xs tracking-widest"
                        >
                            <span className="text-xl">🏁</span>
                            <span className="hidden md:inline">FINALIZAR</span>
                        </button>
                    </div>
                </div>

                {/* TABS */}
                <div className="max-w-7xl mx-auto px-4 border-t border-gray-800 flex justify-center md:justify-start gap-8">
                     <TabButton active={activeTab === 'live'} onClick={() => setActiveTab('live')} label="📡 En Vivo" />
                     <TabButton active={activeTab === 'board'} onClick={() => setActiveTab('board')} label="📋 Pizarra" />
                </div>
            </div>

            {/* Content */}
            <div className={`${isFullScreen ? 'max-w-full px-8' : 'max-w-7xl px-4'} mx-auto py-8 transition-all duration-500`}>
                {activeTab === 'live' ? (
                    <div className="animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Stadium ({visibleCourtKeys.length} Pistas)</h2>
                            <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Progreso: {progress.toFixed(0)}%</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {visibleCourtKeys.map(num => (
                                <LiveCourtCard 
                                    key={num} 
                                    courtNumber={parseInt(num)}
                                    court={getActiveCourt(parseInt(num))} 
                                    onStartMatch={handleStartMatch} 
                                    onFinishMatch={handleFinishMatch}
                                    onUpdateScore={handleUpdateScoreWrapper}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <CourtManager registrations={registrations} eventData={event} 
                        onSaveAssignments={handleSaveAssignments} onInitializeMatches={handleInitializeMatches} />
                )}
            </div>
        </div>
    );
};

const WPTMetric = ({ value, label, type, color = "bg-[#1A1D2B]", size = "normal" }) => {
    const isTimer = type === 'timer' || type === 'timer_active';
    const textColor = type === 'timer_active' ? 'text-[#0F111A]' : 'text-cyan-400';
    const labelBg = type === 'timer_active' ? 'bg-cyan-400' : 'bg-[#252A3D]';

    const heightClass = size === 'large' ? 'min-h-[60px] md:min-h-[80px]' : 'min-h-[45px] md:min-h-[60px]';
    const textSizeClass = size === 'large' ? 'text-3xl md:text-5xl' : 'text-xl md:text-3xl';
    const widthClass = size === 'large' ? 'min-w-[140px] md:min-w-[200px]' : 'min-w-[110px] md:min-w-[140px]';

    return (
        <div className={`flex flex-col ${widthClass} shadow-2xl border border-gray-800 shrink-0`}>
            <div className={`px-2 py-1.5 md:px-4 md:py-3 ${color} flex items-center justify-center ${heightClass}`}>
                <span className={`${textSizeClass} font-black ${isTimer ? 'font-mono' : ''} text-white tracking-widest italic`}>
                    {value || '--:--'}
                </span>
            </div>
            <div className={`${labelBg} px-2 py-1 md:py-1.5 flex items-center justify-center border-t border-gray-800`}>
                <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest ${textColor}`}>
                    {label}
                </span>
            </div>
        </div>
    );
};

const TabButton = ({ active, onClick, label }) => (
    <button onClick={onClick} className={`pb-3 px-2 font-black text-xs uppercase tracking-widest border-b-2 transition-all ${active ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
        {label}
    </button>
);

export default LiveManagement;
